import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminStoreSettings.css';

function AdminStoreSettings() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [formData, setFormData] = useState({
    store_title: '',
    company_name: '',
    company_code: '',
    vat_code: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'Lietuva',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    favicon_url: '',
    footer_logo_url: '',
    instagram_url: '',
    tiktok_url: '',
    amazon_url: '',
    pinterest_url: '',
    bank_name: '',
    bank_account: '',
    bank_swift: '',
    bank_iban: '',
    notes: '',
    maintenance_mode: false
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [footerLogoUploading, setFooterLogoUploading] = useState(false);
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const [showFaviconPreview, setShowFaviconPreview] = useState(false);
  const [showFooterLogoPreview, setShowFooterLogoPreview] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['store-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/store-settings');
      return res.data;
    },
  });

  // Užpildyti formą kai duomenys užkraunami
  React.useEffect(() => {
    if (settings) {
      setFormData({
        store_title: settings.store_title || '',
        company_name: settings.company_name || '',
        company_code: settings.company_code || '',
        vat_code: settings.vat_code || '',
        address_line1: settings.address_line1 || '',
        address_line2: settings.address_line2 || '',
        city: settings.city || '',
        postal_code: settings.postal_code || '',
        country: settings.country || 'Lietuva',
        phone: settings.phone || '',
        email: settings.email || '',
        website: settings.website || '',
        logo_url: settings.logo_url || '',
        favicon_url: settings.favicon_url || '',
        footer_logo_url: settings.footer_logo_url || '',
        instagram_url: settings.instagram_url || '',
        tiktok_url: settings.tiktok_url || '',
        amazon_url: settings.amazon_url || '',
        pinterest_url: settings.pinterest_url || '',
        bank_name: settings.bank_name || '',
        bank_account: settings.bank_account || '',
        bank_swift: settings.bank_swift || '',
        bank_iban: settings.bank_iban || '',
        notes: settings.notes || '',
        maintenance_mode: !!(settings.maintenance_mode)
      });
    }
  }, [settings]);

  // Uždaryti modalą ESC klavišu
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showLogoPreview) {
          setShowLogoPreview(false);
        }
        if (showFaviconPreview) {
          setShowFaviconPreview(false);
        }
        if (showFooterLogoPreview) {
          setShowFooterLogoPreview(false);
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLogoPreview, showFaviconPreview, showFooterLogoPreview]);

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.put('/store-settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['store-settings']);
      notification.success('Parduotuvės duomenys sėkmingai atnaujinti');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant duomenis');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notification.warning('Pasirinkite nuotraukos failą');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notification.info('Nuotraukos dydis negali viršyti 5MB');
      return;
    }

    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiClient.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.imageUrl || response.data.url) {
        const imageUrl = response.data.imageUrl || response.data.url;
        setFormData(prev => ({ ...prev, logo_url: imageUrl }));
        notification.success('Logotipas sėkmingai įkeltas');
        // Reset file input
        e.target.value = '';
      } else {
        notification.error('Nepavyko gauti įkeltos nuotraukos URL');
      }
    } catch (error) {
      notification.error(error.response?.data?.error || 'Nepavyko įkelti logotipo');
      e.target.value = '';
    } finally {
      setLogoUploading(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notification.warning('Pasirinkite nuotraukos failą');
      return;
    }

    // Favicon gali būti .ico, .png, .svg
    const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(ico|png|svg)$/i)) {
      notification.warning('Favicon turi būti .ico, .png arba .svg formato');
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      notification.info('Favicon dydis negali viršyti 1MB');
      return;
    }

    setFaviconUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiClient.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.imageUrl || response.data.url) {
        const imageUrl = response.data.imageUrl || response.data.url;
        setFormData(prev => ({ ...prev, favicon_url: imageUrl }));
        notification.success('Favicon sėkmingai įkeltas');
        // Reset file input
        e.target.value = '';
      } else {
        notification.error('Nepavyko gauti įkeltos nuotraukos URL');
      }
    } catch (error) {
      notification.error(error.response?.data?.error || 'Nepavyko įkelti favicon');
      e.target.value = '';
    } finally {
      setFaviconUploading(false);
    }
  };

  const handleFooterLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notification.warning('Pasirinkite nuotraukos failą');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notification.info('Nuotraukos dydis negali viršyti 5MB');
      return;
    }

    setFooterLogoUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await apiClient.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.imageUrl || response.data.url) {
        const imageUrl = response.data.imageUrl || response.data.url;
        setFormData(prev => ({ ...prev, footer_logo_url: imageUrl }));
        notification.success('Footer logotipas sėkmingai įkeltas');
        e.target.value = '';
      } else {
        notification.error('Nepavyko gauti įkeltos nuotraukos URL');
      }
    } catch (error) {
      notification.error(error.response?.data?.error || 'Nepavyko įkelti footer logotipo');
      e.target.value = '';
    } finally {
      setFooterLogoUploading(false);
    }
  };

  if (isLoading) {
    return <div className="admin-store-settings-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-store-settings-page container">
      <div className="settings-header">
        <h1>Parduotuvės duomenys</h1>
        <p className="settings-subtitle">
          Čia galite nustatyti parduotuvės duomenis, kurie bus naudojami sąskaitose, važtaraščiuose ir kituose dokumentuose.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="store-settings-form">
        {/* Pavadinimas (puslapio / naršyklės skirtuko pavadinimas) */}
        <div className="form-section">
          <h2>Svetainė</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Pavadinimas</label>
              <input
                type="text"
                name="store_title"
                value={formData.store_title}
                onChange={handleChange}
                className="input"
                placeholder="Magtic Parduotuvė"
              />
              <p className="form-help-text">
                Rodomas naršyklės skirtuke ir naudojamas kaip svetainės pavadinimas. Palikite tuščią – bus naudojamas numatytasis „Magtic Parduotuvė”.
              </p>
            </div>
          </div>
        </div>

        {/* Offline / atnaujinimo režimas */}
        <div className="form-section maintenance-mode-section">
          <h2>Parduotuvės režimas</h2>
          <div className="form-group full-width">
            <label className="toggle-label">
              <input
                type="checkbox"
                name="maintenance_mode"
                checked={!!formData.maintenance_mode}
                onChange={(e) => setFormData(prev => ({ ...prev, maintenance_mode: e.target.checked }))}
                className="input-checkbox"
              />
              <span className="toggle-text">Offline / atnaujinimo režimas</span>
            </label>
            <p className="form-help-text">
              Įjungus: išoriniai lankytojai matys pranešimą apie atnaujinimo darbus. Administratorius toliau mato parduotuvę pilna apimtimi.
            </p>
          </div>
        </div>

        {/* Juridiniai duomenys */}
        <div className="form-section">
          <h2>Juridiniai duomenys</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Juridinio asmens pavadinimas *</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="input"
                required
              />
            </div>
            <div className="form-group">
              <label>Juridinio asmens kodas</label>
              <input
                type="text"
                name="company_code"
                value={formData.company_code}
                onChange={handleChange}
                className="input"
              />
            </div>
            <div className="form-group">
              <label>PVM kodas</label>
              <input
                type="text"
                name="vat_code"
                value={formData.vat_code}
                onChange={handleChange}
                className="input"
                placeholder="LT123456789"
              />
            </div>
          </div>
        </div>

        {/* Adresas */}
        <div className="form-section">
          <h2>Adresas</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Adresas (1 eilutė)</label>
              <input
                type="text"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                className="input"
              />
            </div>
            <div className="form-group full-width">
              <label>Adresas (2 eilutė)</label>
              <input
                type="text"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                className="input"
              />
            </div>
            <div className="form-group">
              <label>Miestas</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="input"
              />
            </div>
            <div className="form-group">
              <label>Pašto kodas</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className="input"
              />
            </div>
            <div className="form-group">
              <label>Šalis</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Kontaktai */}
        <div className="form-section">
          <h2>Kontaktai</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Telefono numeris</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="input"
                placeholder="+370 123 45678"
              />
            </div>
            <div className="form-group">
              <label>El. pašto adresas</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="info@example.com"
              />
            </div>
            <div className="form-group">
              <label>Svetainės adresas</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="input"
                placeholder="https://www.example.com"
              />
            </div>
          </div>
        </div>

        {/* Socialinio tinklo nuorodos (footer) */}
        <div className="form-section">
          <h2>Socialinio tinklo nuorodos</h2>
          <p className="form-help-text" style={{ marginBottom: '1rem' }}>
            Nuorodos bus rodomos footeryje šalia favicon. Palikite tuščią, jei nenorite rodyti atitinkamos ikonos.
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>Instagram</label>
              <input
                type="url"
                name="instagram_url"
                value={formData.instagram_url}
                onChange={handleChange}
                className="input"
                placeholder="https://instagram.com/..."
              />
            </div>
            <div className="form-group">
              <label>TikTok</label>
              <input
                type="url"
                name="tiktok_url"
                value={formData.tiktok_url}
                onChange={handleChange}
                className="input"
                placeholder="https://tiktok.com/@..."
              />
            </div>
            <div className="form-group">
              <label>Amazon</label>
              <input
                type="url"
                name="amazon_url"
                value={formData.amazon_url}
                onChange={handleChange}
                className="input"
                placeholder="https://www.amazon.com/..."
              />
            </div>
            <div className="form-group">
              <label>Pinterest</label>
              <input
                type="url"
                name="pinterest_url"
                value={formData.pinterest_url}
                onChange={handleChange}
                className="input"
                placeholder="https://pinterest.com/..."
              />
            </div>
          </div>
        </div>

        {/* Logotipas */}
        <div className="form-section">
          <h2>Logotipas</h2>
          <div className="form-group full-width">
            <label>Parduotuvės logotipas</label>
            <p className="form-help-text">
              Logotipas bus naudojamas PDF sąskaitose faktūrose. Rekomenduojamas formatas: PNG arba SVG, maksimalus dydis: 5MB.
            </p>
            {formData.logo_url && (
              <div className="logo-preview">
                <div className="logo-preview-image-wrapper">
                  <img 
                    src={formData.logo_url} 
                    alt="Logotipas" 
                    onClick={() => setShowLogoPreview(true)}
                    className="logo-preview-image"
                    title="Spustelėkite, kad peržiūrėtumėte didesnį"
                  />
                </div>
                <div className="logo-preview-actions">
                  <button
                    type="button"
                    onClick={() => setShowLogoPreview(true)}
                    className="btn btn-secondary btn-sm"
                  >
                    Peržiūrėti
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Ar tikrai norite pašalinti logotipą?')) {
                        setFormData(prev => ({ ...prev, logo_url: '' }));
                      }
                    }}
                    className="btn btn-danger btn-sm"
                  >
                    Pašalinti
                  </button>
                </div>
              </div>
            )}
            <div className="logo-upload-wrapper">
              <input
                type="file"
                id="logo-upload-input"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={logoUploading}
                className="input"
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="logo-upload-input" 
                className={`logo-upload-label ${logoUploading ? 'disabled' : ''}`}
              >
                {logoUploading ? 'Įkeliama...' : (formData.logo_url ? 'Pakeisti logotipą' : 'Pasirinkti logotipą')}
              </label>
            </div>
          </div>
        </div>

        {/* Favicon */}
        <div className="form-section">
          <h2>Favicon</h2>
          <div className="form-group full-width">
            <label>Parduotuvės favicon</label>
            <p className="form-help-text">
              Favicon bus rodomas naršyklės kortelėje. Rekomenduojamas formatas: .ico, .png arba .svg, maksimalus dydis: 1MB. Rekomenduojamas dydis: 32x32 arba 16x16 pikselių.
            </p>
            {formData.favicon_url && (
              <div className="logo-preview">
                <div className="logo-preview-image-wrapper">
                  <img 
                    src={formData.favicon_url} 
                    alt="Favicon" 
                    onClick={() => setShowFaviconPreview(true)}
                    className="logo-preview-image"
                    title="Spustelėkite, kad peržiūrėtumėte didesnį"
                  />
                </div>
                <div className="logo-preview-actions">
                  <button
                    type="button"
                    onClick={() => setShowFaviconPreview(true)}
                    className="btn btn-secondary btn-sm"
                  >
                    Peržiūrėti
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Ar tikrai norite pašalinti favicon?')) {
                        setFormData(prev => ({ ...prev, favicon_url: '' }));
                      }
                    }}
                    className="btn btn-danger btn-sm"
                  >
                    Pašalinti
                  </button>
                </div>
              </div>
            )}
            <div className="logo-upload-wrapper">
              <input
                type="file"
                id="favicon-upload-input"
                accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,.ico,.png,.svg"
                onChange={handleFaviconUpload}
                disabled={faviconUploading}
                className="input"
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="favicon-upload-input" 
                className={`logo-upload-label ${faviconUploading ? 'disabled' : ''}`}
              >
                {faviconUploading ? 'Įkeliama...' : (formData.favicon_url ? 'Pakeisti favicon' : 'Pasirinkti favicon')}
              </label>
            </div>
          </div>
        </div>

        {/* Footer logotipas */}
        <div className="form-section">
          <h2>Footer logotipas</h2>
          <div className="form-group full-width">
            <label>Logotipas puslapio footeryje</label>
            <p className="form-help-text">
              Jei neįkelsite – footeryje bus rodomas favicon. Rekomenduojamas formatas: .png arba .svg, maksimalus dydis: 5MB.
            </p>
            {formData.footer_logo_url && (
              <div className="logo-preview">
                <div className="logo-preview-image-wrapper">
                  <img
                    src={formData.footer_logo_url}
                    alt="Footer logotipas"
                    onClick={() => setShowFooterLogoPreview(true)}
                    className="logo-preview-image"
                    title="Spustelėkite, kad peržiūrėtumėte didesnį"
                  />
                </div>
                <div className="logo-preview-actions">
                  <button
                    type="button"
                    onClick={() => setShowFooterLogoPreview(true)}
                    className="btn btn-secondary btn-sm"
                  >
                    Peržiūrėti
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Ar tikrai norite pašalinti footer logotipą?')) {
                        setFormData(prev => ({ ...prev, footer_logo_url: '' }));
                      }
                    }}
                    className="btn btn-danger btn-sm"
                  >
                    Pašalinti
                  </button>
                </div>
              </div>
            )}
            <div className="logo-upload-wrapper">
              <input
                type="file"
                id="footer-logo-upload-input"
                accept="image/*"
                onChange={handleFooterLogoUpload}
                disabled={footerLogoUploading}
                className="input"
                style={{ display: 'none' }}
              />
              <label
                htmlFor="footer-logo-upload-input"
                className={`logo-upload-label ${footerLogoUploading ? 'disabled' : ''}`}
              >
                {footerLogoUploading ? 'Įkeliama...' : (formData.footer_logo_url ? 'Pakeisti footer logotipą' : 'Pasirinkti footer logotipą')}
              </label>
            </div>
          </div>
        </div>

        {/* Banko duomenys */}
        <div className="form-section">
          <h2>Banko duomenys</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Banko pavadinimas</label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                className="input"
                placeholder="Swedbank, SEB, Luminor..."
              />
            </div>
            <div className="form-group">
              <label>Sąskaitos numeris</label>
              <input
                type="text"
                name="bank_account"
                value={formData.bank_account}
                onChange={handleChange}
                className="input"
                placeholder="LT12 3456 7890 1234 5678"
              />
            </div>
            <div className="form-group">
              <label>SWIFT/BIC kodas</label>
              <input
                type="text"
                name="bank_swift"
                value={formData.bank_swift}
                onChange={handleChange}
                className="input"
                placeholder="HABALT22"
              />
            </div>
            <div className="form-group">
              <label>IBAN kodas</label>
              <input
                type="text"
                name="bank_iban"
                value={formData.bank_iban}
                onChange={handleChange}
                className="input"
                placeholder="LT12 3456 7890 1234 5678"
              />
            </div>
          </div>
        </div>

        {/* Papildomi pastabos */}
        <div className="form-section">
          <h2>Papildomi pastabos</h2>
          <div className="form-group full-width">
            <label>Pastabos</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="input"
              rows="4"
              placeholder="Papildomi pastabos, kurios gali būti rodomos dokumentuose..."
            />
          </div>
        </div>

        {/* Formos veiksmai */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={updateMutation.isLoading}
            className="btn btn-primary"
          >
            {updateMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti duomenis'}
          </button>
        </div>
      </form>

      {/* Logotipo peržiūros modalas */}
      {showLogoPreview && formData.logo_url && (
        <div 
          className="logo-preview-modal-overlay" 
          onClick={() => setShowLogoPreview(false)}
        >
          <div 
            className="logo-preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="logo-preview-modal-close"
              onClick={() => setShowLogoPreview(false)}
              aria-label="Uždaryti"
            >
              ×
            </button>
            <h3 className="logo-preview-modal-title">Logotipo peržiūra</h3>
            <div className="logo-preview-modal-image-wrapper">
              <img 
                src={formData.logo_url} 
                alt="Logotipas" 
                className="logo-preview-modal-image"
              />
            </div>
            <div className="logo-preview-modal-info">
              <p><strong>URL:</strong> <span className="logo-url">{formData.logo_url}</span></p>
            </div>
          </div>
        </div>
      )}

      {/* Favicon peržiūros modalas */}
      {showFaviconPreview && formData.favicon_url && (
        <div 
          className="logo-preview-modal-overlay" 
          onClick={() => setShowFaviconPreview(false)}
        >
          <div 
            className="logo-preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="logo-preview-modal-close"
              onClick={() => setShowFaviconPreview(false)}
              aria-label="Uždaryti"
            >
              ×
            </button>
            <h3 className="logo-preview-modal-title">Favicon peržiūra</h3>
            <div className="logo-preview-modal-image-wrapper">
              <img 
                src={formData.favicon_url} 
                alt="Favicon" 
                className="logo-preview-modal-image"
                style={{ maxWidth: '128px', maxHeight: '128px' }}
              />
            </div>
            <div className="logo-preview-modal-info">
              <p><strong>URL:</strong> <span className="logo-url">{formData.favicon_url}</span></p>
            </div>
          </div>
        </div>
      )}

      {/* Footer logotipo peržiūros modalas */}
      {showFooterLogoPreview && formData.footer_logo_url && (
        <div 
          className="logo-preview-modal-overlay" 
          onClick={() => setShowFooterLogoPreview(false)}
        >
          <div 
            className="logo-preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="logo-preview-modal-close"
              onClick={() => setShowFooterLogoPreview(false)}
              aria-label="Uždaryti"
            >
              ×
            </button>
            <h3 className="logo-preview-modal-title">Footer logotipo peržiūra</h3>
            <div className="logo-preview-modal-image-wrapper">
              <img 
                src={formData.footer_logo_url} 
                alt="Footer logotipas" 
                className="logo-preview-modal-image"
              />
            </div>
            <div className="logo-preview-modal-info">
              <p><strong>URL:</strong> <span className="logo-url">{formData.footer_logo_url}</span></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminStoreSettings;
